import api from './index'
import type { ApiResponse } from '@/types'
import type {
  FinanceSettings,
  FinanceSettingsInput,
  Contact,
  ContactCreateInput,
  ContactUpdateInput,
  ContactListParams,
  ContactListResponse,
  Unit,
  UnitCreateInput,
  UnitUpdateInput,
  UnitListParams,
  UnitListResponse,
  UnitContactInput,
  ParkingSpace,
  ParkingSpaceCreateInput,
  ParkingSpaceUpdateInput,
  FeeAdjustmentType,
  FeeAdjustmentTypeInput,
  UnitFeeAdjustment,
  UnitFeeAdjustmentInput,
  MonthlyBill,
  BillGenerateInput,
  BillUpdateInput,
  BillAnnouncementUpdateInput,
  BillListParams,
  BillListResponse,
  BillPreviewResponse,
  BankFileUpload,
  BankFileType,
  UploadHistoryResponse,
  UnmatchedRecord,
  UnmatchedListParams,
  UnmatchedListResponse,
  ResolveUnmatchedInput,
  FinanceDashboardSummary,
  MatchedRecord,
  FinancePagination,
  Vendor,
  VendorListParams,
  VendorListResponse,
  VendorCreateInput,
  VendorUpdateInput,
} from '@/types/finance'

// ========== 財務設定 ==========

export const getFinanceSettings = async (
  projectId: string
): Promise<ApiResponse<{ settings: FinanceSettings }>> => {
  const response = await api.get(`/projects/${projectId}/finance/settings`)
  return response.data
}

export const updateFinanceSettings = async (
  projectId: string,
  data: FinanceSettingsInput
): Promise<ApiResponse<{ settings: FinanceSettings }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/settings`, data)
  return response.data
}

// ========== 儀表板 ==========

export const getDashboardSummary = async (
  projectId: string,
  billingPeriod?: string
): Promise<ApiResponse<{ summary: FinanceDashboardSummary; alerts?: FinanceDashboardSummary['alerts'] }>> => {
  const params = billingPeriod ? { billingPeriod } : {}
  const response = await api.get(`/projects/${projectId}/finance/bills/dashboard`, { params })
  return response.data
}

// ========== 聯絡人管理 ==========

export const getContacts = async (
  projectId: string,
  params?: ContactListParams
): Promise<ApiResponse<ContactListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/contacts`, { params })
  return response.data
}

export const getContact = async (
  projectId: string,
  contactId: string
): Promise<ApiResponse<{ contact: Contact }>> => {
  const response = await api.get(`/projects/${projectId}/finance/contacts/${contactId}`)
  return response.data
}

export const createContact = async (
  projectId: string,
  data: ContactCreateInput
): Promise<ApiResponse<{ contact: Contact }>> => {
  const response = await api.post(`/projects/${projectId}/finance/contacts`, data)
  return response.data
}

export const updateContact = async (
  projectId: string,
  contactId: string,
  data: ContactUpdateInput
): Promise<ApiResponse<{ contact: Contact }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/contacts/${contactId}`, data)
  return response.data
}

export const deleteContact = async (
  projectId: string,
  contactId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/contacts/${contactId}`)
  return response.data
}

// ========== 房屋單位管理 ==========

export const getUnits = async (
  projectId: string,
  params?: UnitListParams
): Promise<ApiResponse<UnitListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/units`, { params })
  return response.data
}

export const getUnit = async (
  projectId: string,
  unitId: string
): Promise<ApiResponse<{ unit: Unit }>> => {
  const response = await api.get(`/projects/${projectId}/finance/units/${unitId}`)
  return response.data
}

export const createUnit = async (
  projectId: string,
  data: UnitCreateInput
): Promise<ApiResponse<{ unit: Unit }>> => {
  const response = await api.post(`/projects/${projectId}/finance/units`, data)
  return response.data
}

export const updateUnit = async (
  projectId: string,
  unitId: string,
  data: UnitUpdateInput
): Promise<ApiResponse<{ unit: Unit }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/units/${unitId}`, data)
  return response.data
}

export const deleteUnit = async (
  projectId: string,
  unitId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/units/${unitId}`)
  return response.data
}

// 單位聯絡人關聯
export const addUnitContact = async (
  projectId: string,
  unitId: string,
  data: UnitContactInput
): Promise<ApiResponse<{ unitContact: { id: string } }>> => {
  const response = await api.post(`/projects/${projectId}/finance/units/${unitId}/contacts`, data)
  return response.data
}

export const updateUnitContact = async (
  projectId: string,
  unitId: string,
  unitContactId: string,
  data: { leaseStartDate?: string | null; leaseEndDate?: string | null; isPrimary?: boolean }
): Promise<ApiResponse<{ unitContact: any }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/units/${unitId}/contacts/${unitContactId}`, data)
  return response.data
}

export const removeUnitContact = async (
  projectId: string,
  unitId: string,
  unitContactId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/units/${unitId}/contacts/${unitContactId}`)
  return response.data
}

export const setUnitPayer = async (
  projectId: string,
  unitId: string,
  payerId: string | null
): Promise<ApiResponse<{ unit: Unit }>> => {
  const response = await api.put(`/projects/${projectId}/finance/units/${unitId}/payer`, { payerId })
  return response.data
}

// ========== 停車位管理 ==========

export interface ParkingSpaceListParams {
  page?: number
  limit?: number
  unitId?: string
  ownerId?: string
  type?: string
  search?: string
}

export interface ParkingSpaceListResponse {
  parkingSpaces: ParkingSpace[]
  typeCounts: Record<string, number>
  pagination: FinancePagination
}

export const getParkingSpaces = async (
  projectId: string,
  params?: ParkingSpaceListParams
): Promise<ApiResponse<ParkingSpaceListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/parking-spaces`, { params })
  return response.data
}

export const getParkingSpace = async (
  projectId: string,
  spaceId: string
): Promise<ApiResponse<{ parkingSpace: ParkingSpace }>> => {
  const response = await api.get(`/projects/${projectId}/finance/parking-spaces/${spaceId}`)
  return response.data
}

export const createParkingSpace = async (
  projectId: string,
  data: ParkingSpaceCreateInput
): Promise<ApiResponse<{ parkingSpace: ParkingSpace }>> => {
  const response = await api.post(`/projects/${projectId}/finance/parking-spaces`, data)
  return response.data
}

export const updateParkingSpace = async (
  projectId: string,
  spaceId: string,
  data: ParkingSpaceUpdateInput
): Promise<ApiResponse<{ parkingSpace: ParkingSpace }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/parking-spaces/${spaceId}`, data)
  return response.data
}

export const deleteParkingSpace = async (
  projectId: string,
  spaceId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/parking-spaces/${spaceId}`)
  return response.data
}

// ========== 活化配對 + 租約 ==========

export const activateBikePair = async (
  projectId: string,
  data: { spaceIdA: string; spaceIdB: string }
): Promise<ApiResponse> => {
  const response = await api.post(`/projects/${projectId}/finance/parking-spaces/activate-pair`, data)
  return response.data
}

export const deactivateBikePair = async (
  projectId: string,
  data: { spaceId: string }
): Promise<ApiResponse> => {
  const response = await api.post(`/projects/${projectId}/finance/parking-spaces/deactivate-pair`, data)
  return response.data
}

export const getSpaceLeases = async (
  projectId: string,
  spaceId: string
): Promise<ApiResponse<{ leases: import('@/types/finance').ParkingLease[] }>> => {
  const response = await api.get(`/projects/${projectId}/finance/parking-spaces/${spaceId}/leases`)
  return response.data
}

export interface CreateLeaseInput {
  contactId?: string | null
  unitId?: string | null
  leaseStartDate: string
  leaseEndDate: string
  note?: string | null
  rentAmount?: number | null
  applyToPair?: boolean
}

export const createSpaceLease = async (
  projectId: string,
  spaceId: string,
  data: CreateLeaseInput
): Promise<ApiResponse> => {
  const response = await api.post(`/projects/${projectId}/finance/parking-spaces/${spaceId}/leases`, data)
  return response.data
}

export const updateSpaceLease = async (
  projectId: string,
  leaseId: string,
  data: Partial<CreateLeaseInput> & { status?: 'ACTIVE' | 'ENDED' | 'CANCELLED' }
): Promise<ApiResponse> => {
  const response = await api.patch(`/projects/${projectId}/finance/parking-leases/${leaseId}`, data)
  return response.data
}

export const deleteSpaceLease = async (
  projectId: string,
  leaseId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/parking-leases/${leaseId}`)
  return response.data
}

/**
 * 從 Excel 匯入車位資料
 * @param projectId 專案 ID
 * @param file Excel 檔案
 * @param updateExisting 是否更新已存在的車位
 */
export interface ParkingExcelImportResult {
  created: number
  updated: number
  skipped: number
  errors: string[]
  irregular: Array<{
    row: number
    unitCode: string
    data: string
    reason: string
  }>
  unitNotFound: Array<{
    row: number
    unitCode: string
  }>
}

export const importParkingFromExcel = async (
  projectId: string,
  file: File,
  updateExisting: boolean = false
): Promise<ApiResponse<ParkingExcelImportResult & { importLogId?: string }>> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('updateExisting', String(updateExisting))

  // 不要手動設定 Content-Type，讓 axios 自動處理 FormData 的 boundary
  const response = await api.post(
    `/projects/${projectId}/finance/parking-spaces/import-excel`,
    formData
  )
  return response.data
}

// ========== 車位匯入記錄 ==========

export interface ParkingImportLog {
  id: string
  fileName: string
  importedAt: string
  importedBy: string | null
  totalRows: number
  createdCount: number
  updatedCount: number
  skippedCount: number
  unitNotFoundCount: number
  irregularDataCount: number
  errorsCount: number
}

export interface ParkingImportLogDetail extends Omit<ParkingImportLog, 'unitNotFoundCount' | 'irregularDataCount' | 'errorsCount'> {
  unitNotFound: Array<{ row: number; unitCode: string }>
  irregularData: Array<{ row: number; unitCode: string; data: string; reason: string }>
  errors: string[]
}

export const getParkingImportLogs = async (
  projectId: string,
  params?: { page?: number; limit?: number }
): Promise<ApiResponse<{ logs: ParkingImportLog[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>> => {
  const response = await api.get(`/projects/${projectId}/finance/parking-spaces/import-logs`, { params })
  return response.data
}

export const getParkingImportLogDetail = async (
  projectId: string,
  logId: string
): Promise<ApiResponse<{ log: ParkingImportLogDetail }>> => {
  const response = await api.get(`/projects/${projectId}/finance/parking-spaces/import-logs/${logId}`)
  return response.data
}

export const deleteParkingImportLog = async (
  projectId: string,
  logId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/parking-spaces/import-logs/${logId}`)
  return response.data
}

/**
 * 轉移車位使用權（租賃）
 * - 僅更新使用人 (currentUserId)
 * - 不影響計費單位，管理費仍由所有者繳納
 */
export const transferParkingUsage = async (
  projectId: string,
  spaceId: string,
  newUserId: string | null
): Promise<ApiResponse<{ parkingSpace: ParkingSpace }>> => {
  const response = await api.patch(
    `/projects/${projectId}/finance/parking-spaces/${spaceId}/transfer-usage`,
    { newUserId }
  )
  return response.data
}

/**
 * 轉移車位計費單位（買賣/重新配置）
 * - 更新所有人和計費單位
 * - 預設會清空使用人（解除租賃關係）
 */
export const transferParkingBilling = async (
  projectId: string,
  spaceId: string,
  data: {
    newOwnerId?: string
    newUnitId?: string | null
    clearCurrentUser?: boolean
  }
): Promise<ApiResponse<{ parkingSpace: ParkingSpace }>> => {
  const response = await api.patch(
    `/projects/${projectId}/finance/parking-spaces/${spaceId}/transfer-billing`,
    data
  )
  return response.data
}

// ========== 費用調整類型 ==========

export const getFeeAdjustmentTypes = async (
  projectId: string
): Promise<ApiResponse<{ adjustmentTypes: FeeAdjustmentType[] }>> => {
  const response = await api.get(`/projects/${projectId}/finance/fee-adjustment-types`)
  return response.data
}

export const createFeeAdjustmentType = async (
  projectId: string,
  data: FeeAdjustmentTypeInput
): Promise<ApiResponse<{ adjustmentType: FeeAdjustmentType }>> => {
  const response = await api.post(`/projects/${projectId}/finance/fee-adjustment-types`, data)
  return response.data
}

export const updateFeeAdjustmentType = async (
  projectId: string,
  typeId: string,
  data: Partial<FeeAdjustmentTypeInput>
): Promise<ApiResponse<{ adjustmentType: FeeAdjustmentType }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/fee-adjustment-types/${typeId}`, data)
  return response.data
}

export const deleteFeeAdjustmentType = async (
  projectId: string,
  typeId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/fee-adjustment-types/${typeId}`)
  return response.data
}

// ========== 單位費用調整 ==========

export const getUnitFeeAdjustments = async (
  projectId: string,
  unitId: string,
  billingPeriod?: string
): Promise<ApiResponse<{ adjustments: UnitFeeAdjustment[] }>> => {
  const params = billingPeriod ? { billingPeriod } : {}
  const response = await api.get(`/projects/${projectId}/finance/units/${unitId}/adjustments`, { params })
  return response.data
}

export const createUnitFeeAdjustment = async (
  projectId: string,
  unitId: string,
  data: UnitFeeAdjustmentInput
): Promise<ApiResponse<{ adjustment: UnitFeeAdjustment }>> => {
  const response = await api.post(`/projects/${projectId}/finance/units/${unitId}/adjustments`, data)
  return response.data
}

export const deleteUnitFeeAdjustment = async (
  projectId: string,
  unitId: string,
  adjustmentId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/units/${unitId}/adjustments/${adjustmentId}`)
  return response.data
}

// ========== 帳單管理 ==========

export const getBills = async (
  projectId: string,
  params?: BillListParams
): Promise<ApiResponse<BillListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/bills`, { params })
  return response.data
}

export const getBill = async (
  projectId: string,
  billId: string
): Promise<ApiResponse<{ bill: MonthlyBill }>> => {
  const response = await api.get(`/projects/${projectId}/finance/bills/${billId}`)
  return response.data
}

export const previewBills = async (
  projectId: string,
  params: BillGenerateInput
): Promise<ApiResponse<BillPreviewResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/bills/preview`, { params })
  return response.data
}

export const generateBills = async (
  projectId: string,
  data: BillGenerateInput
): Promise<ApiResponse<{ bills: MonthlyBill[]; count: number }>> => {
  const response = await api.post(`/projects/${projectId}/finance/bills/generate`, data)
  return response.data
}

export interface RecomputeBillsResult {
  billingPeriod: string
  totalBills: number
  updated: number
  unchanged: number
  totalDiff: number
  details: { unitCode: string; before: number; after: number; diff: number }[]
}

export const recomputeBills = async (
  projectId: string,
  data: { billingPeriod: string; unitIds?: string[] }
): Promise<ApiResponse<RecomputeBillsResult>> => {
  const response = await api.post(`/projects/${projectId}/finance/bills/recompute`, data)
  return response.data
}

export const updateBill = async (
  projectId: string,
  billId: string,
  data: BillUpdateInput
): Promise<ApiResponse<{ bill: MonthlyBill }>> => {
  const response = await api.put(`/projects/${projectId}/finance/bills/${billId}`, data)
  return response.data
}

export const deleteBill = async (
  projectId: string,
  billId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/bills/${billId}`)
  return response.data
}

export const batchUpdateBills = async (
  projectId: string,
  billIds: string[],
  data: BillUpdateInput
): Promise<ApiResponse<{ updatedCount: number }>> => {
  const response = await api.put(`/projects/${projectId}/finance/bills/batch`, { billIds, ...data })
  return response.data
}

// ========== 銀行檔案上傳 ==========

export const uploadBankFile = async (
  projectId: string,
  fileType: BankFileType,
  file: File
): Promise<ApiResponse<{ upload: BankFileUpload }>> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('fileType', fileType)

  // Don't set Content-Type manually - let axios set it with the correct boundary
  const response = await api.post(`/projects/${projectId}/finance/bank-files/upload`, formData)
  return response.data
}

export const getUploadHistory = async (
  projectId: string,
  params?: { page?: number; limit?: number; fileType?: BankFileType }
): Promise<ApiResponse<UploadHistoryResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files`, { params })
  return response.data
}

export const getUploadDetail = async (
  projectId: string,
  uploadId: string
): Promise<ApiResponse<{ upload: BankFileUpload }>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files/${uploadId}`)
  return response.data
}

export const deleteBankUpload = async (
  projectId: string,
  uploadId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/bank-files/${uploadId}`)
  return response.data
}

export const getMatchedRecords = async (
  projectId: string,
  uploadId: string,
  params?: { page?: number; limit?: number }
): Promise<ApiResponse<{ records: MatchedRecord[]; pagination: FinancePagination }>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files/${uploadId}/matched`, { params })
  return response.data
}

export const resetUploadMatches = async (
  projectId: string,
  uploadId: string
): Promise<ApiResponse<{ resetCount: number }>> => {
  const response = await api.post(`/projects/${projectId}/finance/bank-files/${uploadId}/reset`)
  return response.data
}

// ========== 未匹配記錄處理 ==========

export const getUnmatchedRecords = async (
  projectId: string,
  params?: UnmatchedListParams
): Promise<ApiResponse<UnmatchedListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files/unmatched`, { params })
  return response.data
}

export const getDuplicateVirtualAccounts = async (
  projectId: string,
  params?: { rangeMonths?: string }
): Promise<ApiResponse<{ groups: import('@/types/finance').DuplicateVirtualAccountGroup[]; totalGroups: number; totalRecords: number }>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files/duplicate-virtual-accounts`, { params })
  return response.data
}

export const resolveUnmatchedRecord = async (
  projectId: string,
  recordId: string,
  data: ResolveUnmatchedInput
): Promise<ApiResponse<{ record: UnmatchedRecord }>> => {
  const response = await api.post(`/projects/${projectId}/finance/bank-files/unmatched/${recordId}/resolve`, data)
  return response.data
}

export const batchResolveUnmatched = async (
  projectId: string,
  recordIds: string[],
  action: 'ignore'
): Promise<ApiResponse<{ resolvedCount: number }>> => {
  const response = await api.post(`/projects/${projectId}/finance/bank-files/unmatched/batch-resolve`, {
    recordIds,
    action,
  })
  return response.data
}

export const reassignUnmatchedRecord = async (
  projectId: string,
  recordId: string,
  data: { billId: string; note?: string }
): Promise<ApiResponse> => {
  const response = await api.post(`/projects/${projectId}/finance/bank-files/unmatched/${recordId}/reassign`, data)
  return response.data
}

export const getMatchableBills = async (
  projectId: string,
  params?: { search?: string; billingPeriod?: string; amount?: number; limit?: number }
): Promise<ApiResponse<{ bills: Array<{
  id: string
  billingPeriod: string
  totalAmount: number
  paymentStatus: string
  unit: { id: string; unitCode: string; address?: string; payer?: { id: string; name: string } | null }
}> }>> => {
  const response = await api.get(`/projects/${projectId}/finance/bank-files/matchable-bills`, { params })
  return response.data
}

export const deleteUnmatchedRecord = async (
  projectId: string,
  recordId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/bank-files/unmatched/${recordId}`)
  return response.data
}

export const batchDeleteUnmatched = async (
  projectId: string,
  recordIds: string[]
): Promise<ApiResponse<{ deletedCount: number }>> => {
  const response = await api.post(`/projects/${projectId}/finance/bank-files/unmatched/batch-delete`, {
    recordIds,
  })
  return response.data
}

// ========== 報表與匯出 ==========

export const exportBills = async (
  projectId: string,
  params: BillListParams & { format?: 'csv' | 'xlsx' }
): Promise<Blob> => {
  const response = await api.get(`/projects/${projectId}/finance/bills/export`, {
    params,
    responseType: 'blob',
  })
  return response.data
}

export const exportContacts = async (
  projectId: string,
  params: ContactListParams & { format?: 'csv' | 'xlsx' } = {}
): Promise<Blob> => {
  const response = await api.get(`/projects/${projectId}/finance/contacts/export`, {
    params,
    responseType: 'blob',
  })
  return response.data
}

export const exportContactOwners = async (
  projectId: string,
  options: { excludeNoOwner?: boolean } = {}
): Promise<Blob> => {
  const response = await api.get(`/projects/${projectId}/finance/contacts/export-owners`, {
    params: options.excludeNoOwner ? { excludeNoOwner: true } : {},
    responseType: 'blob',
  })
  return response.data
}

export const exportUnits = async (
  projectId: string,
  params: UnitListParams & { format?: 'csv' | 'xlsx' }
): Promise<Blob> => {
  const response = await api.get(`/projects/${projectId}/finance/units/export`, {
    params,
    responseType: 'blob',
  })
  return response.data
}

// ========== 帳單公告欄批量更新 ==========

export const batchUpdateAnnouncements = async (
  projectId: string,
  bills: BillAnnouncementUpdateInput[]
): Promise<ApiResponse<{ updated: number }>> => {
  const response = await api.put(`/projects/${projectId}/finance/bills/batch-announcements`, { bills })
  return response.data
}

// ========== 帳單 Excel 匯出 ==========

export const exportBillsXlsx = async (
  projectId: string,
  billingPeriod: string,
  type: 'paper' | 'email' | 'all' = 'all'
): Promise<Blob> => {
  const response = await api.post(
    `/projects/${projectId}/finance/bills/export-xlsx`,
    { billingPeriod, type },
    { responseType: 'blob' }
  )
  return response.data
}

export const exportAchXlsx = async (
  projectId: string,
  billingPeriod: string
): Promise<Blob> => {
  const response = await api.post(
    `/projects/${projectId}/finance/ach/export-xlsx`,
    { billingPeriod },
    { responseType: 'blob' }
  )
  return response.data
}

// ========== 報表匯出 ==========

export const exportUnpaidSummary = async (
  projectId: string,
  options: { building?: string; fromPeriod?: string; toPeriod?: string } = {}
): Promise<Blob> => {
  const params: any = {}
  if (options.building) params.building = options.building
  if (options.fromPeriod) params.fromPeriod = options.fromPeriod
  if (options.toPeriod) params.toPeriod = options.toPeriod
  const response = await api.get(
    `/projects/${projectId}/finance/reports/unpaid-summary`,
    { params, responseType: 'blob' }
  )
  return response.data
}

export const exportUnpaidMatrix = async (
  projectId: string,
  options: { fromPeriod?: string; toPeriod?: string; allPeriods?: boolean } = {}
): Promise<Blob> => {
  const params: any = {}
  if (options.allPeriods) {
    params.allPeriods = 'true'
  } else {
    if (options.fromPeriod) params.fromPeriod = options.fromPeriod
    if (options.toPeriod) params.toPeriod = options.toPeriod
  }
  const response = await api.get(
    `/projects/${projectId}/finance/reports/unpaid-matrix`,
    { params, responseType: 'blob' }
  )
  return response.data
}

// ========== 財務模組 Audit Log ==========

export interface FinanceAuditLogEntry {
  id: string
  action: string
  entityType: string
  entityId?: string | null
  userId: string
  userEmail: string
  description?: string | null
  newValues?: any
  oldValues?: any
  createdAt: string
}

export interface GetFinanceAuditLogsParams {
  section?: 'contacts' | 'units' | 'parking' | 'bills' | 'reconciliation' | 'settings'
  entityType?: string
  entityId?: string
  limit?: number
  before?: string
}

export const getFinanceAuditLogs = async (
  projectId: string,
  params?: GetFinanceAuditLogsParams,
): Promise<ApiResponse<{ logs: FinanceAuditLogEntry[]; hasMore: boolean }>> => {
  const response = await api.get(`/projects/${projectId}/finance/audit-logs`, { params })
  return response.data
}

// ========== 廠商管理 ==========

export const getVendors = async (
  projectId: string,
  params?: VendorListParams
): Promise<ApiResponse<VendorListResponse>> => {
  const response = await api.get(`/projects/${projectId}/finance/vendors`, { params })
  return response.data
}

export const getVendor = async (
  projectId: string,
  vendorId: string
): Promise<ApiResponse<{ vendor: Vendor }>> => {
  const response = await api.get(`/projects/${projectId}/finance/vendors/${vendorId}`)
  return response.data
}

export const createVendor = async (
  projectId: string,
  data: VendorCreateInput
): Promise<ApiResponse<{ vendor: Vendor }>> => {
  const response = await api.post(`/projects/${projectId}/finance/vendors`, data)
  return response.data
}

export const updateVendor = async (
  projectId: string,
  vendorId: string,
  data: VendorUpdateInput
): Promise<ApiResponse<{ vendor: Vendor }>> => {
  const response = await api.patch(`/projects/${projectId}/finance/vendors/${vendorId}`, data)
  return response.data
}

export const deleteVendor = async (
  projectId: string,
  vendorId: string
): Promise<ApiResponse> => {
  const response = await api.delete(`/projects/${projectId}/finance/vendors/${vendorId}`)
  return response.data
}
